import axios from 'axios';
import { useCallback, useState } from 'react';

interface UseApiCallOptions<T, R> {
  apiCall: (data: T) => Promise<R>;
  onSuccess?: (result: R) => void;
  onError?: (error: string) => void;
}

export const useApiCall = <T, R>({ apiCall, onSuccess, onError }: UseApiCallOptions<T, R>) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (data: T) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiCall(data);
        onSuccess?.(result);
        return result;
      } catch (err) {
        let errorMessage = '알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        if (axios.isAxiosError(err)) {
          if (err.response) {
            errorMessage = `${err.response.data.message || err.response.statusText}`;
          } else if (err.request) {
            errorMessage = '서버로부터 응답이 없습니다. 인터넷 연결을 확인해주세요.';
          } else {
            errorMessage = `${err.message}`;
          }
        }
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [apiCall, onSuccess, onError]
  );

  return { execute, error, loading };
};

export default useApiCall;
